/**
 * Carnaf billing page.
 *
 * Shows the current subscription view + an upgrade CTA for FREE users
 * and a "Manage in Stripe" portal link for paid users. Stripe Checkout
 * runs in a full-page redirect (not popup) so the iOS Safari + Android
 * Chrome PWA both handle it consistently.
 *
 * The /billing/success?session_id=... return URL polls /subscription until
 * the webhook lands. We don't wait synchronously because Stripe webhook
 * delivery is asynchronous; the polling UI shows "finalizing your plan…"
 * until the row exists.
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Layout } from '@/components/Layout';
import { Icon } from '@/components/kol/Icon';
import {
  ApiError,
  createCheckoutSession,
  createPortalSession,
  getSubscription,
  type SubscriptionView,
} from '@/lib/api';

export function BillingPage() {
  const { t } = useTranslation();
  const [sub, setSub] = useState<SubscriptionView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await getSubscription();
        if (!cancelled) setSub(data);
      } catch (err) {
        if (!cancelled) setError(err instanceof ApiError ? err.message : t('common.error'));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  async function onCheckout(priceKey: 'PREMIUM_MONTHLY' | 'PREMIUM_ANNUAL') {
    setError(null);
    setPending(true);
    try {
      const { checkoutUrl } = await createCheckoutSession({
        priceKey,
        successUrl: `${window.location.origin}/billing/success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/billing`,
      });
      window.location.href = checkoutUrl;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
      setPending(false);
    }
  }

  async function onPortal() {
    setError(null);
    setPending(true);
    try {
      const { portalUrl } = await createPortalSession(`${window.location.origin}/billing`);
      window.location.href = portalUrl;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
      setPending(false);
    }
  }

  return (
    <Layout
      title={t('billing.title')}
      trailing={
        <Link
          to="/"
          aria-label={t('common.back', { defaultValue: 'חזרה' })}
          className="w-10 h-10 rounded-full border border-carnaf-line bg-carnaf-paper-elevated grid place-items-center text-carnaf-ink-muted shadow-kol-1"
        >
          <Icon name="arrow" size={19} />
        </Link>
      }
    >
      {error && (
        <p role="alert" className="text-carnaf-red mb-4">
          {error}
        </p>
      )}

      {!sub && !error && <p className="text-carnaf-ink-muted">{t('common.loading')}</p>}

      {sub && (
        <div className="space-y-6">
          <CurrentPlanCard sub={sub} />

          {sub.plan === 'FREE' ? (
            <UpgradeCard pending={pending} onCheckout={onCheckout} />
          ) : (
            <ManageCard pending={pending} onPortal={onPortal} />
          )}

          {sub.status === 'PAST_DUE' && (
            <div className="rounded-2xl bg-red-50 border border-red-200 p-4 text-sm text-red-800">
              {t('billing.past_due')}
            </div>
          )}
        </div>
      )}
    </Layout>
  );
}

function CurrentPlanCard({ sub }: { sub: SubscriptionView }) {
  const { t } = useTranslation();
  const formatAmount = () => {
    if (!sub.amountCents) return '—';
    const amount = sub.amountCents / 100;
    const symbol = sub.currency === 'ILS' ? '₪' : sub.currency === 'USD' ? '$' : sub.currency;
    return `${symbol}${amount.toFixed(2)}`;
  };

  return (
    <section className="rounded-2xl bg-white border border-carnaf-line p-5 space-y-3">
      <div className="flex items-baseline justify-between">
        <h2 className="text-lg font-semibold text-carnaf-ink">{t(`billing.plan.${sub.plan.toLowerCase()}`)}</h2>
        <span className="text-[15px] px-2 py-1 rounded-full bg-carnaf-line text-carnaf-ink-muted">
          {t(`billing.status.${sub.status.toLowerCase()}`)}
        </span>
      </div>
      {sub.plan !== 'FREE' && (
        <p className="text-carnaf-ink">
          {formatAmount()} · {sub.currency}
        </p>
      )}
      {sub.currentPeriodEnd && (
        <p className="text-[15px] text-carnaf-ink-muted">
          {t('billing.next_renewal', { date: new Date(sub.currentPeriodEnd).toLocaleDateString() })}
        </p>
      )}
      {sub.cancelAtPeriodEnd && (
        <p className="text-[15px] text-carnaf-amber-ink">{t('billing.cancels_at_period_end')}</p>
      )}
    </section>
  );
}

function UpgradeCard({
  pending,
  onCheckout,
}: {
  pending: boolean;
  onCheckout: (k: 'PREMIUM_MONTHLY' | 'PREMIUM_ANNUAL') => void;
}) {
  const { t } = useTranslation();
  return (
    <section className="rounded-2xl bg-white border border-carnaf-line p-5 space-y-4">
      <h2 className="text-lg font-semibold text-carnaf-ink">{t('billing.upgrade_title')}</h2>
      <ul className="text-sm text-carnaf-ink-muted space-y-1.5 list-disc list-inside">
        <li>{t('billing.feat.vehicles')}</li>
        <li>{t('billing.feat.kb')}</li>
        <li>{t('billing.feat.accident')}</li>
        <li>{t('billing.feat.family')}</li>
        <li>{t('billing.feat.sticker')}</li>
      </ul>
      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={() => onCheckout('PREMIUM_MONTHLY')}
          disabled={pending}
          className="rounded-2xl border-2 border-carnaf-teal text-carnaf-teal-dark font-semibold py-3 disabled:opacity-50"
        >
          {t('billing.monthly')}
          <span className="block text-[15px] text-carnaf-ink-muted">{t('billing.monthly_price')}</span>
        </button>
        <button
          onClick={() => onCheckout('PREMIUM_ANNUAL')}
          disabled={pending}
          className="rounded-2xl bg-carnaf-teal text-white font-semibold py-3 disabled:opacity-50"
        >
          {t('billing.annual')}
          <span className="block text-[15px] opacity-80">{t('billing.annual_price')}</span>
        </button>
      </div>
      <p className="text-[15px] text-carnaf-ink-muted text-center">{t('billing.vat_note')}</p>
    </section>
  );
}

function ManageCard({ pending, onPortal }: { pending: boolean; onPortal: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      onClick={onPortal}
      disabled={pending}
      className="w-full rounded-2xl bg-carnaf-teal text-white font-semibold py-4 disabled:opacity-50"
    >
      {pending ? t('common.loading') : t('billing.manage')}
    </button>
  );
}

// --- /billing/success post-checkout return URL ---

export function CheckoutSuccessPage() {
  const { t } = useTranslation();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const [sub, setSub] = useState<SubscriptionView | null>(null);
  const [tries, setTries] = useState(0);
  const sessionId = search.get('session_id');

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    async function tick() {
      try {
        const s = await getSubscription();
        if (cancelled) return;
        if (s.plan !== 'FREE') {
          setSub(s);
          setTimeout(() => navigate('/billing', { replace: true }), 800);
          return;
        }
      } catch {
        /* keep polling */
      }
      setTries((n) => n + 1);
      if (tries < 15 && !cancelled) {
        timer = setTimeout(tick, 1500);
      }
    }
    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [navigate, tries]);

  return (
    <Layout title={t('billing.success_title')}>
      {!sub && (
        <div className="rounded-2xl bg-white border border-carnaf-line p-6 text-center space-y-3">
          <div className="size-8 mx-auto rounded-full border-2 border-carnaf-line border-t-carnaf-teal animate-spin" />
          <p className="text-carnaf-ink-muted">{t('billing.finalizing')}</p>
          {sessionId && <p className="text-[15px] text-carnaf-ink-muted font-mono">{sessionId.slice(0, 24)}…</p>}
        </div>
      )}
      {sub && (
        <div className="rounded-2xl bg-white border border-carnaf-line p-6 text-center">
          <h2 className="text-lg font-semibold text-carnaf-teal-dark">{t('billing.welcome_to_premium')}</h2>
          <p className="text-sm text-carnaf-ink-muted mt-2">{t(`billing.plan.${sub.plan.toLowerCase()}`)}</p>
        </div>
      )}
    </Layout>
  );
}
